import { useEffect, useState, type FormEvent } from 'react';
import { fetchServers, type ServerRequest, type ServerView } from '../api/servers';
import { ca } from '../i18n/ca';
import { ModDownloads } from './ModDownloads';
import { PlannedChip } from './Planned';

/**
 * The Creator DLCs a server can load, by the panel's short id and the name the
 * platform shows (ArmaDlc). A new server loads none until somebody ticks one:
 * a server made with the reference server's four by default was a mistake
 * (the owner, 2026-10-17).
 */
const CREATOR_DLCS: [id: string, name: string][] = [
  ['vn', 'S.O.G. Prairie Fire'],
  ['spe', 'Spearhead 1944'],
  ['gm', 'Global Mobilization'],
  ['ws', 'Western Sahara'],
  ['csla', 'CSLA Iron Curtain'],
  ['rf', 'Reaction Forces'],
  ['ef', 'Expeditionary Forces'],
];

/** A server's DLCs, given by name, as the panel's ids. */
function dlcIds(names: string[] | undefined): string[] {
  const wanted = (names ?? []).map((n) => n.toLowerCase());
  return CREATOR_DLCS.filter(([, n]) => wanted.includes(n.toLowerCase())).map(([id]) => id);
}

/**
 * The "New server" drawer and, with a server, the configure form (UI doc
 * 5.12). Fields grouped in four sections; the port is assigned by the platform.
 *
 * The mods come from another server, copied with its Creator DLCs as they are
 * now, or from a Launcher preset file, read here for its list of mods and kept
 * nowhere — a convenience the owner asked for (2026-09-19): the file is only
 * ever a list. Never from a preset on the panel, which the platform does not
 * read.
 */
export function ServerForm({
  server,
  budgetLeft,
  onSubmit,
  onCancel,
  error,
  downloading = [],
  inline = false,
}: {
  server: ServerView | null;
  budgetLeft: number;
  onSubmit: (input: ServerRequest) => Promise<void>;
  onCancel: () => void;
  error: string | null;
  /** The Workshop ids the panel was just told to download for this request: their progress shows under the error. */
  downloading?: number[];
  /** Inside a page (the server's detail) rather than the list's drawer. */
  inline?: boolean;
}) {
  const [others, setOthers] = useState<ServerView[]>([]);
  const [name, setName] = useState(server?.name ?? '');
  const [description, setDescription] = useState(server?.description ?? '');
  // '' keeps or leaves no mods; a server id copies that server's; 'file' reads the Launcher preset.
  const [modsFrom, setModsFrom] = useState<string>('');
  const [launcherPreset, setLauncherPreset] = useState<string | null>(null);
  const [maxPlayers, setMaxPlayers] = useState(server?.maxPlayers ?? 24);
  const [password, setPassword] = useState('');
  const [battlEye, setBattlEye] = useState(server?.battlEye ?? true);
  const [headlessClients, setHeadlessClients] = useState(server?.lifecycle?.headlessClients ?? 1);
  const [missionTemplate, setMissionTemplate] = useState(server?.missionTemplate ?? '');
  const [startBeforeMinutes, setStartBefore] = useState(
    server?.lifecycle?.startBeforeMinutes ?? 15,
  );
  const [stopIdleMinutes, setStopIdle] = useState(server?.lifecycle?.stopIdleMinutes ?? 30);
  const [keepForCampaign, setKeep] = useState(server?.lifecycle?.keepForCampaign ?? false);
  const [retentionDays, setRetention] = useState(server?.lifecycle?.retentionDays ?? 7);
  const [dlcs, setDlcs] = useState<string[]>(dlcIds(server?.dlcs));
  // Editing sends the DLCs only once a box is touched: a server whose view
  // carries no list would otherwise lose the ones it runs.
  const [dlcsTouched, setDlcsTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchServers()
      .then((r) => setOthers(r.servers.filter((x) => x.id !== server?.id)))
      .catch(() => setOthers([]));
  }, [server?.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await onSubmit({
        name,
        description: description || undefined,
        mods:
          modsFrom === ''
            ? null
            : modsFrom === 'file'
              ? { launcherPreset: launcherPreset ?? '' }
              : { fromServerId: Number(modsFrom) },
        activeDlcs: !server || dlcsTouched ? dlcs : undefined,
        maxPlayers,
        password: password || (server ? undefined : ''),
        battlEye,
        headlessClients,
        missionTemplate: missionTemplate || undefined,
        startBeforeMinutes,
        stopIdleMinutes,
        keepForCampaign,
        retentionDays,
      });
    } finally {
      setBusy(false);
    }
  }

  const title = server ? ca.configure : ca.newServer;
  const s = ca.serversPage;
  return (
    <form className={`card form ${inline ? '' : 'drawer'}`} onSubmit={submit} aria-label={title}>
      <h2>{title}</h2>
      <p className="muted small">
        {server ? `${ca.port}: ${server.port}` : ca.portAssigned}
        {!server && ` ${ca.budgetRoom(Math.max(0, budgetLeft))}`}
      </p>

      <div className="section-group">
        <h2 className="section">{s.sectionBasic}</h2>
        <label>
          {ca.serverName}
          <input
            name="name"
            required
            maxLength={64}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          {ca.serverDescription}
          <input
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label>
          {s.modsFrom}
          <select
            name="modsFrom"
            value={modsFrom}
            onChange={(e) => {
              const from = e.target.value;
              setModsFrom(from);
              // Copying another server's mods proposes its DLCs too, shown and untickable.
              const source = others.find((o) => String(o.id) === from);
              if (source && !dlcsTouched) setDlcs(dlcIds(source.dlcs));
            }}
          >
            <option value="">{server ? s.modsKeep : s.modsNone}</option>
            <optgroup label={s.modsFromServer}>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} · {s.modsCount(o.activeMods?.length ?? 0)}
                </option>
              ))}
            </optgroup>
            <option value="file">{s.modsFromFile}</option>
          </select>
        </label>
        {modsFrom === 'file' && (
          <label>
            {s.modsFile}
            <input
              name="launcherPreset"
              type="file"
              accept=".html,text/html"
              required
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return setLauncherPreset(null);
                void file.text().then(setLauncherPreset);
              }}
            />
            <span className="muted small">{s.modsFileHint}</span>
          </label>
        )}
        <fieldset className="dlcs">
          <legend>{ca.dlcs}</legend>
          {CREATOR_DLCS.map(([id, dlcName]) => (
            <label key={id} className="check">
              <input
                type="checkbox"
                name={`dlc-${id}`}
                checked={dlcs.includes(id)}
                onChange={(e) => {
                  setDlcsTouched(true);
                  setDlcs((now) => (e.target.checked ? [...now, id] : now.filter((d) => d !== id)));
                }}
              />
              {dlcName}
            </label>
          ))}
          <span className="muted small">{ca.dlcsHint}</span>
        </fieldset>
        <label>
          {ca.missionTemplate}
          <input
            name="missionTemplate"
            value={missionTemplate}
            onChange={(e) => setMissionTemplate(e.target.value)}
          />
        </label>
      </div>

      <div className="section-group">
        <h2 className="section">{s.sectionPlayers}</h2>
        <div className="two">
          <label>
            {ca.maxPlayers}
            <input
              name="maxPlayers"
              type="number"
              min={1}
              max={200}
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
            />
          </label>
          <label>
            {ca.headlessClients}
            <input
              name="headlessClients"
              type="number"
              min={0}
              max={3}
              value={headlessClients}
              onChange={(e) => setHeadlessClients(Number(e.target.value))}
            />
          </label>
        </div>
        <label>
          {ca.serverPassword}
          <input
            name="password"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={battlEye}
            onChange={(e) => setBattlEye(e.target.checked)}
          />
          {ca.battlEye}
        </label>
      </div>

      <div className="section-group">
        <h2 className="section">{s.sectionLifecycle}</h2>
        <div className="two">
          <label>
            {ca.startBefore}
            <input
              name="startBeforeMinutes"
              type="number"
              min={0}
              value={startBeforeMinutes}
              onChange={(e) => setStartBefore(Number(e.target.value))}
            />
          </label>
          <label>
            {ca.stopIdle}
            <input
              name="stopIdleMinutes"
              type="number"
              min={0}
              value={stopIdleMinutes}
              onChange={(e) => setStopIdle(Number(e.target.value))}
            />
          </label>
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={keepForCampaign}
            onChange={(e) => setKeep(e.target.checked)}
          />
          {ca.keepForCampaign}
        </label>
        {!keepForCampaign && (
          // 0 days is "never": the lifecycle deletes a server only when its
          // retention is above 0 (ServerLifecycleScheduler).
          <label className="check">
            <input
              type="checkbox"
              name="neverDelete"
              checked={retentionDays === 0}
              onChange={(e) => setRetention(e.target.checked ? 0 : 7)}
            />
            {ca.neverDelete}
          </label>
        )}
        {!keepForCampaign && retentionDays !== 0 && (
          <label>
            {ca.retentionDays}
            <input
              name="retentionDays"
              type="number"
              min={1}
              value={retentionDays}
              onChange={(e) => setRetention(Math.max(1, Number(e.target.value)))}
            />
          </label>
        )}
      </div>

      <div className="section-group">
        <h2 className="section">{s.sectionAdvanced}</h2>
        <fieldset className="two">
          <label>
            <span>
              {ca.difficulty} <PlannedChip id="server-difficulty" />
            </span>
            <select disabled aria-label={ca.difficulty}>
              <option>—</option>
            </select>
          </label>
          <label>
            <span>
              {ca.advancedParameters} <PlannedChip id="server-advanced-parameters" />
            </span>
            <input disabled aria-label={ca.advancedParameters} />
          </label>
        </fieldset>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {downloading.length > 0 && <ModDownloads ids={downloading} />}
      <div className="row actions">
        <button type="button" className="ghost" onClick={onCancel}>
          {ca.cancel}
        </button>
        <span className="grow" />
        <button type="submit" className="primary" disabled={busy}>
          {server ? ca.save : ca.create}
        </button>
      </div>
    </form>
  );
}
